import { useCallback, useEffect, useMemo, useState } from 'react';
import { Plus, Settings2, X } from 'lucide-react';
import { api } from '../api/client';
import { useSocle } from '../hooks/useSocle';
import { fmtCHF, fmtCHF2 } from '../utils/format';
import { GrilleLoyers, LigneSite, ScenarioBudget, TagRevenu, MOIS_LABELS } from '../types';
import { BadgeTagRevenu, ChoixTagScenario } from '../components/TagScenarioRevenu';
import { HistoriqueRevenu } from '../components/HistoriqueRevenu';
import { ScenariosModal } from '../components/ScenariosModal';
import { SelectAnnee } from '../components/SelectAnnee';

/**
 * Loyers : un lieu loué (site) = fiche (nom interne, adresse, m², note) +
 * loyer mensuel et charges mensuelles événementiels (mois_effet, tag,
 * commentaire — pattern salaires). Clic sur le nom → fiche du site ;
 * double-clic sur une cellule → valeur figée puis « Corriger ».
 */
export function LoyersPage() {
  const maintenant = new Date();
  const [annee, setAnnee] = useState(maintenant.getFullYear());
  // undefined = socle pas encore connu ; la grille attend avant de charger
  const socleId = useSocle();
  const [scenarioId, setScenarioId] = useState<number | null | undefined>(undefined);
  const [affichage, setAffichage] = useState<'total' | 'loyer' | 'charges'>('total');
  const [grille, setGrille] = useState<GrilleLoyers | null>(null);
  const [erreur, setErreur] = useState<string | null>(null);
  const [chargement, setChargement] = useState(true);
  const [scenariosOuverts, setScenariosOuverts] = useState(false);
  const [ficheSite, setFicheSite] = useState<LigneSite | null | 'nouveau'>(null);
  const [edition, setEdition] = useState<{ site: LigneSite; mois: number } | null>(null);

  const charger = useCallback(() => {
    if (scenarioId === undefined) return; // on attend de connaître le socle
    setErreur(null);
    api.get<GrilleLoyers>(`/loyers/grille?annee=${annee}&scenario=${scenarioId ?? 'definitif'}`)
      .then(setGrille)
      .catch((e) => setErreur(e.message))
      .finally(() => setChargement(false));
  }, [annee, scenarioId]);

  useEffect(() => {
    if (scenarioId === undefined && socleId !== undefined) setScenarioId(socleId);
  }, [socleId, scenarioId]);

  useEffect(() => { charger(); }, [charger]);

  // Scénario affiché supprimé ou archivé : revenir au socle (le budget), et aux
  // décisions seules seulement si c'est le socle lui-même qui a disparu
  useEffect(() => {
    if (grille && scenarioId != null && !grille.scenarios.some((s) => s.id === scenarioId && !s.archive)) {
      setScenarioId(scenarioId === socleId ? null : socleId ?? null);
    }
  }, [grille, scenarioId, socleId]);

  const scenariosActifs = useMemo(
    () => (grille?.scenarios ?? []).filter((s) => !s.archive),
    [grille]
  );
  const variantes = useMemo(() => scenariosActifs.filter((s) => s.id !== socleId), [scenariosActifs, socleId]);
  const scenarioNom = scenarioId != null
    ? scenariosActifs.find((s) => s.id === scenarioId)?.nom ?? null
    : null;

  const moisCourant = annee === maintenant.getFullYear()
    ? maintenant.getMonth() + 1
    : annee < maintenant.getFullYear() ? 13 : 0;
  const classesColonne = (m: number) =>
    m < moisCourant ? ' col-passe' : m === moisCourant ? ' col-courant' : '';

  const surfaceTotale = useMemo(
    () => (grille?.sites ?? []).reduce((s, l) => s + (l.m2 ?? 0), 0),
    [grille]
  );

  // Sites regroupés par catégorie (ordre d'apparition conservé) ; les sites
  // sans groupe passent en fin sous « Autres »
  const groupes = useMemo(() => {
    const ordre: string[] = [];
    const parGroupe = new Map<string, LigneSite[]>();
    for (const s of grille?.sites ?? []) {
      const g = s.groupe ?? 'Autres';
      if (!parGroupe.has(g)) { parGroupe.set(g, []); ordre.push(g); }
      parGroupe.get(g)!.push(s);
    }
    const sansGroupe = ordre.filter((g) => g === 'Autres');
    return [...ordre.filter((g) => g !== 'Autres'), ...sansGroupe]
      .map((g) => ({ groupe: g, sites: parGroupe.get(g)! }));
  }, [grille]);
  const avecGroupes = groupes.length > 1 || (grille?.sites ?? []).some((s) => s.groupe != null);

  const valeurCellule = (s: LigneSite, i: number) =>
    affichage === 'total' ? s.mois[i].total : affichage === 'loyer' ? s.mois[i].loyer : s.mois[i].charges;
  const valeurAnnuelle = (s: LigneSite) =>
    affichage === 'total' ? s.totalAnnuel : affichage === 'loyer' ? s.totalLoyer : s.totalCharges;

  function renduCellule(site: LigneSite, m: number) {
    const c = site.mois[m - 1];
    const projete = c.tagLoyer === 'projete' || c.tagCharges === 'projete';
    const changement = c.changementLoyer ?? c.changementCharges;
    const moisIso = `${annee}-${String(m).padStart(2, '0')}`;
    // Mois futur dont loyer ET charges ne sont que des reports = prévision
    const reporte = (me: string | null) => me != null && me.slice(0, 7) < moisIso;
    const previsionReportee = !projete && m > moisCourant &&
      (c.loyer !== 0 || c.charges !== 0) &&
      reporte(c.moisEffetLoyer) && (c.charges === 0 || reporte(c.moisEffetCharges));

    const valeur = affichage === 'total' ? c.total : affichage === 'loyer' ? c.loyer : c.charges;
    const secondaire = affichage === 'total' && c.loyer !== 0 && c.charges !== 0
      ? `${fmtCHF(c.loyer)} + ${fmtCHF(c.charges)}`
      : '';

    const infobulle = [
      `${site.nom} — ${MOIS_LABELS[m - 1]} ${annee}`,
      `Loyer CHF ${fmtCHF2(c.loyer)} · charges CHF ${fmtCHF2(c.charges)} = CHF ${fmtCHF2(c.total)}`,
      previsionReportee ? 'Prévision : report de la dernière valeur en vigueur (aucune décision ne prend effet ce mois-ci)' : null,
      c.changementLoyer ? `Changement de loyer (${c.changementLoyer.tag}) : ${c.changementLoyer.commentaire}` : null,
      c.changementCharges ? `Changement de charges (${c.changementCharges.tag}) : ${c.changementCharges.commentaire}` : null,
      'Double-cliquer pour modifier',
    ].filter(Boolean).join('\n');

    return (
      <td
        key={m}
        className={`cell-mois${classesColonne(m)}${projete ? ' cell-projete' : ''}${valeur === 0 ? ' cell-vide' : ''}${previsionReportee ? ' cell-report' : ''}`}
        title={infobulle}
        onDoubleClick={() => setEdition({ site, mois: m })}
      >
        {changement && <span className={`cell-flag cell-flag--${changement.tag === 'projete' ? 'projete' : 'definitif'}`} />}
        <span className="cell-principal">{valeur !== 0 ? fmtCHF(valeur) : '—'}</span>
        {secondaire && <span className="cell-secondaire">{secondaire}</span>}
      </td>
    );
  }

  if (chargement) {
    return <div className="flex items-center justify-center" style={{ height: 240 }}><div className="spinner" /></div>;
  }

  const totalAffiche = grille?.totalAnnuel ?? { loyer: 0, charges: 0, total: 0 };
  const totauxMois = grille?.totauxMensuels[affichage] ?? new Array(12).fill(0);

  return (
    <div>
      <div className="page-header">
        <div className="flex items-center gap-8">
          <h1 className="page-title">Loyers</h1>
          <span className="stat-pill">CHF {fmtCHF(totalAffiche.total)} / {annee}</span>
          {scenarioId == null
            ? <span className="stat-pill" style={{ color: 'var(--ink-2)' }}
                title="Décisions arrêtées seulement — les mois à venir n'y sont qu'un report">décidé seulement</span>
            : scenarioId === socleId
              ? <span className="stat-pill" style={{ color: 'var(--accent)' }}
                  title="Le budget en l'état : décisions arrêtées plus hypothèses retenues">budget</span>
              : <span className="stat-pill" style={{ color: 'var(--warn)' }}>variante · {scenarioNom}</span>}
        </div>
        <div className="flex gap-8 items-center">
          <SelectAnnee annee={annee} onChange={setAnnee} />
          <button className="btn btn-accent btn-sm" onClick={() => setFicheSite('nouveau')}>
            <Plus className="icon-sm" /> Nouveau site
          </button>
        </div>
      </div>

      {erreur && <div className="card mb-16 p-16 text-err">{erreur}</div>}

      <div className="kpi-row mb-16">
        <div className="kpi-card">
          <span className="kpi-card__label">Loyers {annee}</span>
          <span className="kpi-card__value">{fmtCHF(totalAffiche.loyer)}<span className="kpi-card__suffix">CHF</span></span>
        </div>
        <div className="kpi-card">
          <span className="kpi-card__label">Charges {annee}</span>
          <span className="kpi-card__value">{fmtCHF(totalAffiche.charges)}<span className="kpi-card__suffix">CHF</span></span>
        </div>
        <div className="kpi-card">
          <span className="kpi-card__label">Sites actifs</span>
          <span className="kpi-card__value">{grille?.sites.length ?? 0}</span>
        </div>
        <div className="kpi-card">
          <span className="kpi-card__label">Surface totale</span>
          <span className="kpi-card__value">{fmtCHF(surfaceTotale)}<span className="kpi-card__suffix">m²</span></span>
        </div>
      </div>

      <div className="toolbar">
        <div className="selectable-group" title="Grandeur affichée dans les cellules">
          <button className={`selectable${affichage === 'total' ? ' active' : ''}`} onClick={() => setAffichage('total')}>Total</button>
          <button className={`selectable${affichage === 'loyer' ? ' active' : ''}`} onClick={() => setAffichage('loyer')}>Loyer</button>
          <button className={`selectable${affichage === 'charges' ? ' active' : ''}`} onClick={() => setAffichage('charges')}>Charges</button>
        </div>
        <div className="flex gap-8 items-center" title="Décisions prises seules, ou avec les hypothèses d'un scénario">
          <div className="selectable-group">
            <button
              className={`selectable${scenarioId != null && scenarioId === socleId ? ' active' : ''}`}
              disabled={socleId == null}
              title="Le budget en l'état : décisions arrêtées plus hypothèses retenues"
              onClick={() => setScenarioId(socleId ?? null)}
            >
              Budget
            </button>
            <button
              className={`selectable${scenarioId != null && scenarioId !== socleId ? ' active' : ''}`}
              disabled={variantes.length === 0}
              title={variantes.length === 0 ? 'Aucune variante — en créer une dans Scénarios' : 'Une variante du budget'}
              onClick={() => setScenarioId(variantes[0]?.id ?? null)}
            >
              Variante
            </button>
            <button
              className={`selectable${scenarioId == null ? ' active' : ''}`}
              title="Décisions arrêtées seulement, sans aucune hypothèse — les mois à venir n'y sont qu'un report"
              onClick={() => setScenarioId(null)}
            >
              Décidé seulement
            </button>
          </div>
          {scenarioId != null && scenarioId !== socleId && (
            <select className="sort-select" value={scenarioId} onChange={(e) => setScenarioId(Number(e.target.value))}>
              {variantes.map((s) => <option key={s.id} value={s.id}>{s.nom}</option>)}
            </select>
          )}
          <button className="btn btn-icon" title="Gérer les scénarios" onClick={() => setScenariosOuverts(true)}>
            <Settings2 className="icon-sm" />
          </button>
        </div>
      </div>

      <div className="grille-container">
        <table className="table table-compact grille-salaires">
          <thead>
            <tr>
              <th className="cell-nom">Site</th>
              {MOIS_LABELS.map((label, i) => (
                <th key={label} className={`cell-mois${classesColonne(i + 1)}`}>
                  {label}
                  {i + 1 === moisCourant && <span className="marqueur-courant" title="Mois en cours" />}
                </th>
              ))}
              <th className="cell-total">Total {annee}</th>
            </tr>
          </thead>
          <tbody>
            {groupes.map(({ groupe, sites }) => [
              avecGroupes && (
                <tr key={`g-${groupe}`} className="ligne-groupe">
                  <td className="cell-nom">{groupe}</td>
                  {Array.from({ length: 12 }, (_, i) => {
                    const v = sites.reduce((s, l) => s + valeurCellule(l, i), 0);
                    return (
                      <td key={i} className={`cell-mois${classesColonne(i + 1)}`}>
                        <span className="cell-principal">{v !== 0 ? fmtCHF(v) : ''}</span>
                      </td>
                    );
                  })}
                  <td className="cell-total">
                    <span className="cell-principal">
                      {fmtCHF(sites.reduce((s, l) => s + valeurAnnuelle(l), 0))}
                    </span>
                  </td>
                </tr>
              ),
              ...sites.map((site) => (
                <tr key={site.id} className="ligne-employe">
                  <td className="cell-nom">
                    <span
                      className="nom-employe"
                      style={{ cursor: 'pointer', paddingLeft: avecGroupes ? 12 : 0 }}
                      title={[site.adresse, site.note].filter(Boolean).join('\n') || 'Ouvrir la fiche du site'}
                      onClick={() => setFicheSite(site)}
                    >
                      {site.nom}
                    </span>
                    {site.aProjections && <span className="badge badge-warn badge-sm" title="Comporte des valeurs projetées">proj.</span>}
                    <span className="text-xs text-ink-3" style={{ display: 'block', paddingLeft: avecGroupes ? 12 : 0 }}>
                      {[
                        site.m2 != null ? `${fmtCHF(site.m2)} m²` : null,
                        site.chfM2An != null ? `CHF ${fmtCHF(site.chfM2An)}/m²/an` : null,
                      ].filter(Boolean).join(' · ')}
                    </span>
                  </td>
                  {Array.from({ length: 12 }, (_, i) => renduCellule(site, i + 1))}
                  <td className="cell-total">
                    <span className="cell-principal">{fmtCHF(valeurAnnuelle(site))}</span>
                  </td>
                </tr>
              )),
            ])}
            {(grille?.sites.length ?? 0) === 0 && (
              <tr><td colSpan={14} className="text-ink-3 p-16">Aucun site — créer le premier avec « Nouveau site ».</td></tr>
            )}
            {(grille?.sites.length ?? 0) > 0 && (
              <tr className="ligne-groupe">
                <td className="cell-nom">Total ({affichage === 'total' ? 'loyers + charges' : affichage})</td>
                {totauxMois.map((v: number, i: number) => (
                  <td key={i} className={`cell-mois${classesColonne(i + 1)}`}>
                    <span className="cell-principal">{v !== 0 ? fmtCHF(v) : '—'}</span>
                  </td>
                ))}
                <td className="cell-total">
                  <span className="cell-principal">
                    {fmtCHF(affichage === 'total' ? totalAffiche.total : affichage === 'loyer' ? totalAffiche.loyer : totalAffiche.charges)}
                  </span>
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <p className="text-xs text-ink-3 mt-8">
        Loyer et charges mensuels par site, résolus « en vigueur » : une valeur s'applique dès son mois d'effet
        jusqu'au prochain changement (indexation, ajustement des charges). <strong>Double-cliquer sur une
        cellule</strong> pour voir la valeur figée puis la corriger ; <strong>cliquer sur le nom du site</strong> pour
        la fiche (adresse, m², historiques). Les valeurs projetées appartiennent à un scénario.
      </p>

      {edition && grille && (
        <EditionLoyerMois
          site={edition.site}
          mois={edition.mois}
          annee={annee}
          scenarios={grille.scenarios}
          scenarioCourant={scenarioId ?? null}
          moisCourant={moisCourant}
          onFermer={() => setEdition(null)}
          onModifie={() => { setEdition(null); charger(); }}
        />
      )}
      {ficheSite && (
        <SiteModal
          site={ficheSite === 'nouveau' ? null : ficheSite}
          scenarios={grille?.scenarios ?? []}
          onFermer={() => setFicheSite(null)}
          onModifie={() => { setFicheSite(null); charger(); }}
        />
      )}
      {scenariosOuverts && (
        <ScenariosModal onFermer={() => setScenariosOuverts(false)} onModifie={charger} />
      )}
    </div>
  );
}

// ── Édition d'un mois (loyer + charges) ────────────────────────────────────

function EditionLoyerMois({ site, mois, annee, scenarios, scenarioCourant, moisCourant, onFermer, onModifie }: {
  site: LigneSite;
  mois: number;
  annee: number;
  scenarios: ScenarioBudget[];
  scenarioCourant: number | null;
  moisCourant: number;
  onFermer: () => void;
  onModifie: () => void;
}) {
  const cellule = site.mois[mois - 1];
  const [moisEffet, setMoisEffet] = useState(`${annee}-${String(mois).padStart(2, '0')}`);
  const [loyer, setLoyer] = useState(String(cellule.loyer));
  const [charges, setCharges] = useState(String(cellule.charges));
  const [tag, setTag] = useState<'definitif' | 'projete'>(scenarioCourant != null ? 'projete' : 'definitif');
  const [scenarioId, setScenarioId] = useState<number | null>(scenarioCourant);
  const [commentaireManuel, setCommentaireManuel] = useState<string | null>(null);
  const [erreur, setErreur] = useState<string | null>(null);
  const [enCours, setEnCours] = useState(false);
  // La valeur affichée est figée : il faut cliquer « Corriger » pour saisir.
  const [deverrouille, setDeverrouille] = useState(false);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onFermer(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onFermer]);

  const nomScenario = (id: number | null) =>
    id == null ? null : scenarios.find((s) => s.id === id)?.nom ?? `#${id}`;

  // Mois futur dont la valeur n'est qu'un report = prévision, pas un définitif
  const futur = mois > moisCourant;
  const badgeValeur = (t: TagRevenu | null, moisEffetEvt: string | null) => {
    if (!t) return null;
    if (t !== 'projete' && futur && moisEffetEvt != null &&
        moisEffetEvt.slice(0, 7) < `${annee}-${String(mois).padStart(2, '0')}`) {
      const [a, m] = moisEffetEvt.split('-');
      return (
        <span
          className="badge badge-warn badge-sm"
          title={`Prévision : report du dernier ${t === 'consolide' ? 'consolidé' : 'définitif'} en vigueur (${MOIS_LABELS[Number(m) - 1].toLowerCase()} ${a}). Aucune décision ne prend effet ce mois-ci.`}
        >
          prévision
        </span>
      );
    }
    return <BadgeTagRevenu tag={t} scenarioNom={nomScenario(scenarioCourant)} />;
  };

  const loyerNum = Number(loyer);
  const chargesNum = Number(charges);
  const loyerChange = Math.abs(loyerNum - cellule.loyer) > 0.004;
  const chargesChange = Math.abs(chargesNum - cellule.charges) > 0.004;

  const commentaireAuto = useMemo(() => {
    const parts: string[] = [];
    if (loyerChange) parts.push(`loyer ${fmtCHF2(cellule.loyer)} → ${fmtCHF2(loyerNum)}`);
    if (chargesChange) parts.push(`charges ${fmtCHF2(cellule.charges)} → ${fmtCHF2(chargesNum)}`);
    return `Édition : ${parts.length ? parts.join(', ') : 'sans changement'}, dès ${moisEffet}`;
  }, [loyerChange, chargesChange, cellule, loyerNum, chargesNum, moisEffet]);
  const commentaire = commentaireManuel ?? commentaireAuto;

  const valide =
    /^\d{4}-\d{2}$/.test(moisEffet) &&
    (loyerChange || chargesChange) &&
    loyerNum >= 0 && chargesNum >= 0 &&
    commentaire.trim().length > 0 &&
    (tag === 'definitif' || scenarioId != null);

  async function enregistrer() {
    if (!valide || enCours) return;
    setEnCours(true);
    setErreur(null);
    const base = tag === 'projete' ? { tag, scenarioId } : { tag };
    try {
      if (loyerChange) {
        await api.post(`/loyers/sites/${site.id}/loyer-evenements`, {
          moisEffet, montant: loyerNum, ...base, commentaire: commentaire.trim(),
        });
      }
      if (chargesChange) {
        await api.post(`/loyers/sites/${site.id}/charges-evenements`, {
          moisEffet, montant: chargesNum, ...base, commentaire: commentaire.trim(),
        });
      }
      onModifie();
    } catch (e) {
      setErreur((e as Error).message);
      setEnCours(false);
    }
  }

  return (
    <div className="modal-overlay" onClick={onFermer}>
      <div className="modal" style={{ maxWidth: 560 }} onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <div className="modal-title">{site.nom} — {MOIS_LABELS[mois - 1]} {annee}</div>
          <button className="close-btn" onClick={onFermer} title="Fermer (Esc)"><X className="icon-sm" /></button>
        </div>
        <div className="modal-body" style={{ overflowY: 'auto' }}>
          {!deverrouille ? (
            <div className="formulaire-changement mb-16">
              <div className="flex flex-col gap-8">
                <div className="flex items-center gap-8 text-sm">
                  <span>Loyer : <strong>CHF {fmtCHF2(cellule.loyer)}</strong></span>
                  {badgeValeur(cellule.tagLoyer, cellule.moisEffetLoyer)}
                </div>
                <div className="flex items-center gap-8 text-sm">
                  <span>Charges : <strong>CHF {fmtCHF2(cellule.charges)}</strong></span>
                  {badgeValeur(cellule.tagCharges, cellule.moisEffetCharges)}
                </div>
                <div className="text-sm text-ink-2">Total : CHF {fmtCHF2(cellule.total)}</div>
              </div>
              <div className="flex items-center justify-between mt-8">
                <span className="text-xs text-ink-3">Valeur figée — corriger crée un nouvel événement tracé.</span>
                <button className="btn btn-accent btn-sm" onClick={() => setDeverrouille(true)}>Corriger ✎</button>
              </div>
            </div>
          ) : (
          <div className="formulaire-changement mb-16">
            <div className="formulaire-changement__titre">Nouvelle valeur à partir d'un mois donné</div>
            <div className="flex gap-12 flex-wrap items-end">
              <div className="form-group">
                <label className="form-label">À partir de</label>
                <input type="month" className="form-input" value={moisEffet} onChange={(e) => setMoisEffet(e.target.value)} />
              </div>
              <div className="form-group">
                <label className="form-label">Loyer (CHF/mois)</label>
                <input type="number" className="form-input" style={{ width: 120 }} min={0} step={0.05} autoFocus
                  value={loyer} onChange={(e) => setLoyer(e.target.value)} onFocus={(e) => e.target.select()} />
              </div>
              <div className="form-group">
                <label className="form-label">Charges (CHF/mois)</label>
                <input type="number" className="form-input" style={{ width: 120 }} min={0} step={0.05}
                  value={charges} onChange={(e) => setCharges(e.target.value)} onFocus={(e) => e.target.select()} />
              </div>
              <div className="form-group">
                <label className="form-label">Statut</label>
                <ChoixTagScenario tag={tag} scenarioId={scenarioId} scenarios={scenarios}
                  onChange={(t, s) => { setTag(t); setScenarioId(s); }} />
              </div>
            </div>
            <div className="form-group mt-8">
              <label className="form-label">Commentaire (obligatoire)</label>
              <input className="form-input w-full" value={commentaire}
                onChange={(e) => setCommentaireManuel(e.target.value)} />
            </div>
            <p className="text-xs text-ink-3">
              Seules les valeurs modifiées créent un événement — loyer et charges gardent chacun leur historique.
            </p>
            <div className="flex justify-end">
              <button className="btn btn-accent" disabled={!valide || enCours} onClick={enregistrer}>
                {enCours ? 'Enregistrement…' : 'Enregistrer'}
              </button>
            </div>
          </div>
          )}

          {erreur && <div className="text-err text-sm mb-8">{erreur}</div>}
        </div>
      </div>
    </div>
  );
}

// ── Fiche du site (création / édition + historiques) ───────────────────────

function SiteModal({ site, scenarios, onFermer, onModifie }: {
  site: LigneSite | null; // null = création
  scenarios: ScenarioBudget[];
  onFermer: () => void;
  onModifie: () => void;
}) {
  const [nom, setNom] = useState(site?.nom ?? '');
  const [adresse, setAdresse] = useState(site?.adresse ?? '');
  const [m2, setM2] = useState(site?.m2 != null ? String(site.m2) : '');
  const [groupe, setGroupe] = useState(site?.groupe ?? '');
  const [note, setNote] = useState(site?.note ?? '');
  const [actif, setActif] = useState(site?.actif ?? true);
  const [commentaire, setCommentaire] = useState('');
  const [erreur, setErreur] = useState<string | null>(null);
  const [enCours, setEnCours] = useState(false);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onFermer(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onFermer]);

  const nomScenario = (id: number | null) =>
    id == null ? null : scenarios.find((s) => s.id === id)?.nom ?? `#${id}`;

  const valide = nom.trim().length > 0 && commentaire.trim().length > 0 &&
    (m2 === '' || Number(m2) > 0);

  async function enregistrer() {
    if (!valide || enCours) return;
    setEnCours(true);
    setErreur(null);
    const corps = {
      nom: nom.trim(), adresse: adresse.trim() || null, m2: m2 === '' ? null : Number(m2),
      groupe: groupe.trim() || null, note: note.trim() || null, actif, commentaire: commentaire.trim(),
    };
    try {
      if (site) await api.put(`/loyers/sites/${site.id}`, corps);
      else await api.post('/loyers/sites', corps);
      onModifie();
    } catch (e) {
      setErreur((e as Error).message);
      setEnCours(false);
    }
  }

  async function supprimerEvenement(type: 'loyer' | 'charges', id: number) {
    const c = window.prompt('Commentaire de suppression (obligatoire) :');
    if (!c || !c.trim()) return;
    setErreur(null);
    try {
      await api.delete(`/loyers/${type}-evenements/${id}`, { commentaire: c.trim() });
      onModifie();
    } catch (e) {
      setErreur((e as Error).message);
    }
  }

  return (
    <div className="modal-overlay" onClick={onFermer}>
      <div className="modal" style={{ maxWidth: 640 }} onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <div className="modal-title">{site ? site.nom : 'Nouveau site'}</div>
          <button className="close-btn" onClick={onFermer} title="Fermer (Esc)"><X className="icon-sm" /></button>
        </div>
        <div className="modal-body" style={{ overflowY: 'auto' }}>
          <div className="formulaire-changement mb-16">
            <div className="flex gap-12 flex-wrap items-end">
              <div className="form-group">
                <label className="form-label">Nom interne</label>
                <input className="form-input" style={{ width: 180 }} autoFocus={!site}
                  value={nom} onChange={(e) => setNom(e.target.value)} />
              </div>
              <div className="form-group">
                <label className="form-label">Surface (m²)</label>
                <input type="number" className="form-input" style={{ width: 100 }} min={0} step={0.5}
                  value={m2} onChange={(e) => setM2(e.target.value)} />
              </div>
              <div className="form-group">
                <label className="form-label">Groupe</label>
                <input className="form-input" style={{ width: 150 }} placeholder="Loyers CCC…"
                  title="Catégorie de regroupement dans la grille (sous-totaux)"
                  value={groupe} onChange={(e) => setGroupe(e.target.value)} />
              </div>
              {site && (
                <div className="form-group">
                  <label className="form-label">Actif</label>
                  <input type="checkbox" checked={actif} onChange={(e) => setActif(e.target.checked)}
                    title="Un site inactif disparaît de la grille (historique conservé)" />
                </div>
              )}
            </div>
            <div className="form-group mt-8">
              <label className="form-label">Adresse</label>
              <input className="form-input w-full" value={adresse} onChange={(e) => setAdresse(e.target.value)} />
            </div>
            <div className="form-group">
              <label className="form-label">Note</label>
              <input className="form-input w-full" placeholder="Bail, particularités…"
                value={note} onChange={(e) => setNote(e.target.value)} />
            </div>
            <div className="form-group">
              <label className="form-label">Commentaire (obligatoire)</label>
              <input className="form-input w-full" placeholder={site ? 'Motif de la modification…' : 'Source des informations…'}
                value={commentaire} onChange={(e) => setCommentaire(e.target.value)} />
            </div>
            <div className="flex justify-end">
              <button className="btn btn-accent" disabled={!valide || enCours} onClick={enregistrer}>
                {enCours ? 'Enregistrement…' : site ? 'Enregistrer' : 'Créer le site'}
              </button>
            </div>
          </div>

          {erreur && <div className="text-err text-sm mb-8">{erreur}</div>}

          {site && (
            <>
              <HistoriqueRevenu
                titre="Historique du loyer"
                lignes={site.evenementsLoyer.map((e) => ({
                  id: e.id, mois: e.mois_effet, valeur: `CHF ${fmtCHF2(e.montant)}`,
                  tag: e.tag, scenarioNom: nomScenario(e.scenario_id), commentaire: e.commentaire,
                }))}
                onSupprimer={(id) => supprimerEvenement('loyer', id)}
              />
              <HistoriqueRevenu
                titre="Historique des charges"
                lignes={site.evenementsCharges.map((e) => ({
                  id: e.id, mois: e.mois_effet, valeur: `CHF ${fmtCHF2(e.montant)}`,
                  tag: e.tag, scenarioNom: nomScenario(e.scenario_id), commentaire: e.commentaire,
                }))}
                onSupprimer={(id) => supprimerEvenement('charges', id)}
              />
              {site.evenementsLoyer.length === 0 && site.evenementsCharges.length === 0 && (
                <p className="text-xs text-ink-3">
                  Aucun loyer saisi — double-cliquer sur une cellule de la grille pour poser la première valeur.
                </p>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
